import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTeams } from "@/contexts/teams-provider";
import {
  canDecideAutoUse,
  autoEnabledAfterOwnerChange,
  canTakeAutoUsedTacticOut,
} from "@/components/prompt/auto-use-permission";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { PromptDTO, PromptOwnerType } from "@shaarp/common";
import { AlertTriangleIcon, ChevronLeftIcon } from "lucide-react";
import { FormEvent, useId, useRef, useState } from "react";
import { ButtonLoading } from "../button-loading";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { Switch } from "../ui/switch";
import { TextareaAutosize } from "../ui/textarea-autosize";
import { useToast, useToastError } from "../ui/use-toast";

export default function DialogEditPrompt({
  prompt,
  onSaved = () => {},
  onClose = () => {},
  variant = "dialog",
}: {
  prompt: PromptDTO;
  onSaved?: () => void;
  onClose?: () => void;
  variant?: "dialog" | "sheet";
}) {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();

  const { isManager, isAdmin, isGod, teamSelected } = useTeams();

  const [title, setTitle] = useState<string>(prompt.title || "");
  const [description, setDescription] = useState<string>(
    prompt.description || "",
  );
  const [content, setContent] = useState<string>(prompt.content);
  const [ownerType, setOwnerType] = useState<PromptOwnerType>(prompt.ownerType);
  const [isAutoEnabled, setIsAutoEnabled] = useState<boolean>(
    prompt.isAutoEnabled,
  );
  // Whether the author has clicked the switch themselves. Until they have, a
  // change of type moves it to that type's value; once they have, their
  // explicit choice is never overridden again.
  const hasTouchedAutoEnabled = useRef(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const autoUseHelperId = useId();
  const autoUseScopeId = useId();
  const autoUseReasonId = useId();

  // The switch decides for everyone the tactic belongs to, so a team's is the
  // manager's call. Mirrored in the API (`assertCanSetAutoUse`), which is what
  // actually enforces it — this only spares a member a refused save.
  const canSetAutoUse = canDecideAutoUse({
    ownerType,
    selectedTeamRole: teamSelected?.userRole,
    isPlatformGod: isGod,
  });

  // Handing the tactic to someone else is the same decision seen from the other
  // side: a switched-on team tactic pulled back into a personal one stops
  // applying to every colleague. Read off the saved tactic, not off the form —
  // what matters is who is being taken from.
  const canTakeOut = canTakeAutoUsedTacticOut({
    currentOwnerType: prompt.ownerType,
    isAutoEnabled: prompt.isAutoEnabled,
    selectedTeamRole: teamSelected?.userRole,
    isPlatformGod: isGod,
  });

  // Every explanation shown next to the switch, in reading order.
  const describedBy = [
    autoUseHelperId,
    ownerType === "team" ? autoUseScopeId : null,
    canSetAutoUse ? null : autoUseReasonId,
  ]
    .filter(Boolean)
    .join(" ");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await api.post(`/prompt/${prompt.id}`, {
        prompt: {
          title,
          content,
          description,
          ownerType,
          categoryId: prompt.categoryId,
          isAutoEnabled,
        },
      });
      toast({
        variant: "default",
        title: t("prompt.edit.success.title"),
        description: t("prompt.edit.success.description"),
      });
      onSaved();
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const formFields = (
    <div className="space-y-5">
      {(isManager || isAdmin) && (
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="ownerType">
            {t("prompt.edit.fields.type.label")}
          </Label>
          <p className="text-xs text-gray-500">
            {t("prompt.edit.fields.type.helper")}
          </p>
          <Select
            value={ownerType}
            onValueChange={(newValue) => {
              const nextOwnerType = newValue as PromptOwnerType;
              setOwnerType(nextOwnerType);
              if (!hasTouchedAutoEnabled.current) {
                setIsAutoEnabled(
                  autoEnabledAfterOwnerChange({
                    savedOwnerType: prompt.ownerType,
                    savedIsAutoEnabled: prompt.isAutoEnabled,
                    nextOwnerType,
                  }),
                );
              }
            }}
            disabled={!canTakeOut}
          >
            <SelectTrigger>
              <SelectValue defaultValue="user" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="user">
                  {t("prompt.card.types.user")}
                </SelectItem>
                {(isManager || isAdmin) && (
                  <SelectItem value="team">
                    {t("prompt.card.types.team")}
                  </SelectItem>
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
          {!canTakeOut && (
            <p className="text-xs text-amber-600">
              {t("prompt.edit.fields.type.autoUsedByTeam")}
            </p>
          )}
        </div>
      )}
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor="title">{t("prompt.edit.fields.title.label")}</Label>
        <p className="text-xs text-gray-500">
          {t("prompt.edit.fields.title.helper")}
        </p>
        <Input
          type="text"
          id="title"
          placeholder={t("prompt.edit.fields.title.placeholder")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </div>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor="description">
          {t("prompt.edit.fields.description.label")}
        </Label>
        <p className="text-xs text-gray-500">
          {t("prompt.edit.fields.description.helper")}
        </p>
        <TextareaAutosize
          id="description"
          placeholder={t("prompt.edit.fields.description.placeholder")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {description.trim().length === 0 && (
          <div className="flex gap-2 p-3 text-xs leading-snug border rounded-md border-warning/40 bg-warning/10 text-foreground">
            <AlertTriangleIcon
              className="shrink-0 size-4 mt-0.5 text-warning"
              aria-hidden
            />
            <p>{t("prompt.edit.fields.description.missing")}</p>
          </div>
        )}
      </div>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <Label htmlFor="isAutoEnabled">
            {t("prompt.edit.fields.autoUse.label")}
          </Label>
          {/* Worded by owner type, as in the creation dialog: editing is where a
              tactic already shared changes scope, so it is where the reach has
              to be spelled out. */}
          <p id={autoUseHelperId} className="text-xs text-gray-500">
            {t("prompt.edit.fields.autoUse.helper")}
          </p>
          {/* The reach, and only when there is one to state: switching a team's
              tactic on commits conversations the author will never see. */}
          {ownerType === "team" && (
            <p id={autoUseScopeId} className="mt-1 text-xs text-warning">
              {t("prompt.edit.fields.autoUse.scopeTeam")}
            </p>
          )}
          {!canSetAutoUse && (
            <p id={autoUseReasonId} className="mt-1 text-xs text-muted-foreground">
              {t("prompt.edit.fields.autoUse.managerOnly")}
            </p>
          )}
        </div>
        {/* Disabled rather than hidden: a member still has to be able to read
            whether the tactic they are editing steers the assistant.

            No `aria-label`: it would override the `<Label htmlFor>` beside it,
            leaving a screen reader with "Automatic use, switch" and none of the
            reach or the reason it is greyed out. */}
        <Switch
          id="isAutoEnabled"
          checked={isAutoEnabled}
          onCheckedChange={(checked) => {
            hasTouchedAutoEnabled.current = true;
            setIsAutoEnabled(checked);
          }}
          disabled={!canSetAutoUse}
          aria-describedby={describedBy}
        />
      </div>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor="content">
          {t("prompt.edit.fields.content.label")}
        </Label>
        <p className="text-xs text-gray-500">
          {t("prompt.edit.fields.content.helper")}
        </p>
        <TextareaAutosize
          id="content"
          placeholder={t("prompt.edit.fields.content.placeholder")}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          required
        />
      </div>
    </div>
  );

  if (variant === "sheet") {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="relative flex items-center justify-center px-5 pt-3 pb-3 shrink-0 border-b">
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.back")}
            className="absolute left-3 inline-flex items-center justify-center rounded-full size-9 hover:bg-muted active:bg-muted transition-colors"
          >
            <ChevronLeftIcon className="size-5" />
          </button>
          <DialogTitle className="text-lg font-semibold">
            {t("prompt.edit.title")}
          </DialogTitle>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
            {formFields}
          </div>
          <div className="shrink-0 border-t px-5 py-4">
            <ButtonLoading
              isLoading={isLoading}
              type="submit"
              className="w-full"
            >
              {t("prompt.edit.buttons.save")}
            </ButtonLoading>
          </div>
        </form>
      </div>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[800px] w-full">
        <DialogHeader>
          <DialogTitle>{t("prompt.edit.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <Separator className="my-4" />
          <div className="overflow-auto max-h-[70vh] px-3">{formFields}</div>
          <Separator className="my-4" />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">{t("prompt.edit.buttons.close")}</Button>
            </DialogClose>
            <ButtonLoading isLoading={isLoading} type="submit">
              {t("prompt.edit.buttons.save")}
            </ButtonLoading>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
